import type { Doc } from 'yjs';
import { ICommand } from './ICommand';
import type { Edge, Node } from '../models/scene';
import { useDiagramStore } from '../../stores/diagramStore';
import { getNodeMutationManager } from './mutations/NodeMutationManager';
import {
  applyRdmSceneInWorkspace,
  replaceSceneInWorkspace,
} from '../../lib/diagram/replaceSceneInYDoc';
import { validateFinalShapeCountOrPromptUpgrade } from '../../utils/shapeLimitUtils';

export interface ApplyRdmSceneParams {
  /** The fully laid-out runtime nodes to write to the canvas. */
  nodes: Node[];
  /** The fully laid-out runtime edges to write to the canvas. */
  edges: Edge[];
  emptySceneTarget?: { doc: Doc | null; bindingVersion?: number };
}

export class RdmSceneApplyError extends Error {
  constructor(readonly code: 'SCENE_CHANGED' | 'SHAPE_LIMIT', message: string) {
    super(message);
    this.name = 'RdmSceneApplyError';
  }
}

/**
 * ApplyRdmSceneCommand — materialize an RDM-derived scene onto the canvas.
 *
 * The agent's `rdm_apply` tool generates RDM text, parses + lays it out into
 * a runtime model (`rdmService.toLayoutedRuntimeModel`), and then needs to
 * write that scene to the live diagram. This command is the undoable bridge:
 * it snapshots the current scene, merges the generated RDM-owned content into
 * the live workspace, and restores the snapshot on undo.
 *
 * RDM authoring still re-emits the full RDM-owned diagram so connectors and
 * swimlane banding stay consistent, but apply/redo must not clear unrelated
 * canvas content that was created manually or by another concurrent writer.
 * One RDM apply = one undo step.
 */
export class ApplyRdmSceneCommand implements ICommand {
  id: string;
  name: string;
  description?: string;
  timestamp: number;

  private readonly nextNodes: Node[];
  private readonly nextEdges: Edge[];
  private readonly emptySceneTarget?: { doc: Doc | null; bindingVersion?: number };
  private appliedSceneSignature?: string;
  private prevNodes: Node[] | null = null;
  private prevEdges: Edge[] | null = null;

  constructor(params: ApplyRdmSceneParams) {
    this.id = `apply-rdm-scene-${Date.now()}`;
    this.name = 'Apply RDM';
    this.description = `Apply RDM scene (${params.nodes.length} nodes, ${params.edges.length} edges)`;
    this.timestamp = Date.now();
    this.nextNodes = params.nodes;
    this.nextEdges = params.edges;
    this.emptySceneTarget = params.emptySceneTarget;
  }

  execute(): void {
    this.assertEmptyTarget();
    // Snapshot the current scene once so undo can restore it. Deep-clone so
    // later store mutations can't bleed into the captured snapshot.
    if (this.prevNodes === null) {
      const state = useDiagramStore.getState();
      this.prevNodes = state.nodes.map((n) => structuredClone(n));
      this.prevEdges = state.edges.map((e) => structuredClone(e));
    }
    this.applyRdmScene(this.nextNodes, this.nextEdges);
    if (this.emptySceneTarget) this.appliedSceneSignature = this.sceneSignature();
  }

  undo(): void {
    if (this.prevNodes === null || this.prevEdges === null) return;
    if (this.emptySceneTarget) {
      this.assertDocumentTarget();
      if (this.sceneSignature() !== this.appliedSceneSignature) {
        throw new RdmSceneApplyError('SCENE_CHANGED', 'The diagram changed after generation. Review the current diagram before undoing.');
      }
    }
    this.restoreScene(this.prevNodes, this.prevEdges);
  }

  redo(): void {
    this.assertEmptyTarget();
    this.applyRdmScene(this.nextNodes, this.nextEdges);
    if (this.emptySceneTarget) this.appliedSceneSignature = this.sceneSignature();
  }

  private assertDocumentTarget(): void {
    const manager = getNodeMutationManager();
    if (this.emptySceneTarget && ((manager.getDoc() ?? null) !== this.emptySceneTarget.doc
      || (this.emptySceneTarget.bindingVersion !== undefined && (manager.getDocumentBindingVersion?.() ?? 0) !== this.emptySceneTarget.bindingVersion))) {
      throw new RdmSceneApplyError('SCENE_CHANGED', 'The active diagram changed. Refresh its context before generating a process.');
    }
  }

  private assertEmptyTarget(): void {
    if (!this.emptySceneTarget) return;
    this.assertDocumentTarget();
    const state = useDiagramStore.getState();
    const doc = this.emptySceneTarget.doc;
    if (state.nodes.length || state.edges.length || doc?.getMap('shapes').size || doc?.getMap('connectors').size) {
      throw new RdmSceneApplyError('SCENE_CHANGED', 'The diagram is no longer empty. Refresh context and use propose_process_changes for edits.');
    }
  }

  private sceneSignature(): string {
    const doc = this.emptySceneTarget?.doc;
    if (doc) return JSON.stringify([doc.getMap('shapes').toJSON(), doc.getMap('connectors').toJSON()]);
    const state = useDiagramStore.getState();
    return JSON.stringify([state.nodes, state.edges]);
  }

  private applyRdmScene(nodes: Node[], edges: Edge[]): void {
    const doc = getNodeMutationManager().getDoc() ?? null;
    const state = useDiagramStore.getState();
    applyRdmSceneInWorkspace({
      doc,
      nodes,
      edges,
      currentScene: {
        nodes: state.nodes,
        edges: state.edges,
      },
      loadScene: state.loadScene,
      validateFinalScene: (scene) => {
        const permitted = validateFinalShapeCountOrPromptUpgrade(state, scene.nodes.length + scene.edges.length);
        if (!permitted && this.emptySceneTarget) {
          throw new RdmSceneApplyError('SHAPE_LIMIT', 'The generated process exceeds the current diagram allowance. Resolve plan access before applying.');
        }
        return permitted;
      },
    });
  }

  private restoreScene(nodes: Node[], edges: Edge[]): void {
    const doc = getNodeMutationManager().getDoc() ?? null;
    replaceSceneInWorkspace({
      doc,
      nodes,
      edges,
      loadScene: useDiagramStore.getState().loadScene,
    });
  }
}
